import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { IcoPlus } from 'icone';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Button } from 'react-bootstrap';
import PeriodicReportingActionCell from './PeriodicReportingActionCell';
import AG_GRID_LOCALE_IT from '../../ag-grid/locale-it';
import { PeriodicReportingType } from '../PeriodicReporting';
import { useLocation } from 'react-router-dom-v5-compat';
import { getTotalsByProjectId, TotalsType } from 'lib/api_ts/periodicReportingApi';
import { formatToDecimals } from 'lib/helpers/formatting';
import { columnCentered, columnToRight } from 'lib/helpers/agGridHelpers';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import PeriodicReportingCheckCell from './PeriodicReportingCheckCell';
import { UserContext } from 'components/contexts/UserProvider';
interface PeriodicReportingTableProps {
	rowData: PeriodicReportingType[];
	setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	fetchData: () => void;
	state: 'DRAFT' | 'VALIDATED';
	isEnabledToView: boolean;
	isProjectWp: boolean;
	userCompanies: number[];
	projectIndirectCostsPerc: number;
}

const PeriodicReportingTable: React.FC<PeriodicReportingTableProps> = ({
	rowData,
	setIsCreateModalOpen,
	fetchData,
	state,
	isEnabledToView,
	isProjectWp,
	userCompanies,
	projectIndirectCostsPerc,
}) => {
	const { userAccess, isCoordinator, isPartnerUser } = useContext(UserContext);
	const hasPartnerAccess = isProjectWp ? isPartnerUser : isCoordinator;
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const [totals, setTotals] = useState<TotalsType>();
	const [isLoadingTotals, setIsLoadingTotals] = useState(true);

	const fetchTotals = useCallback(async () => {
		setIsLoadingTotals(true);
		try {
			const totalsFromApi = await getTotalsByProjectId(projectId, state);
			setTotals(totalsFromApi);
			setIsLoadingTotals(false);
		} catch (error) {
			console.error('error', error);
		}
	}, [projectId, state]);

	useEffect(() => {
		fetchTotals();
	}, [fetchTotals]);

	const localeText = {
		...AG_GRID_LOCALE_IT,
		noRowsToShow: 'Non hai ancora fatto alcun report',
	};
	const columnDefs = [
		{
			headerName: 'Tipo',
			field: 'periodicReportingNumber',
			sortable: true,
		},
		{
			headerName: 'Importo Rendicontato €',
			headerTooltip: 'Importo Rendicontato €',
			valueGetter: (params: { data: { id: number } }) => {
				if (isLoadingTotals) return 0;
				const requestedAmount = totals.statements?.find((statement) => statement.statementId === params.data.id);
				const requestedAmountTotal = requestedAmount?.total;
				return formatToDecimals(requestedAmountTotal || 0);
			},
			...columnToRight(),
		},
		{
			headerName: 'Contributo Massimo Ottenibile €',
			headerTooltip: 'Contributo Massimo Ottenibile €',
			valueGetter: (params: { data: { requestedAmount: number } }) => {
				return formatToDecimals(params.data.requestedAmount);
			},
			...columnToRight(),
		},
		...(state === 'VALIDATED'
			? [
					{
						headerName: 'Contributo Richiesto €',
						headerTooltip: 'Contributo Richiesto €',
						field: 'amount',
						valueGetter: (params: { data: { amount: number } }) => {
							return formatToDecimals(params.data.amount);
						},
						...columnToRight(),
					},
				]
			: []),
		...(state === 'VALIDATED'
			? [
					{
						headerName: 'Inviato',
						field: 'sent',
						cellRenderer: PeriodicReportingCheckCell,
						minWidth: 170,
						maxWidth: 170,
						...columnCentered(),
					},
					{
						headerName: 'Mese inviato',
						field: 'monthsSentDate',
						minWidth: 120,
						valueGetter: (params: { data: { monthsSentDate: number } }) => {
							if (!params.data.monthsSentDate) return '';
							return 'M' + params.data.monthsSentDate;
						},
						...columnCentered(),
					},
					{
						headerName: 'Contributo Approvato €',
						headerTooltip: 'Contributo Approvato €',
						field: 'approvedAmount',
						valueGetter: (params: { data: { approvedAmount: number } }) => {
							return formatToDecimals(params.data.approvedAmount);
						},
						...columnToRight(),
					},
					{
						headerName: 'Approvato',
						field: 'approved',
						cellRenderer: PeriodicReportingCheckCell,
						// minWidth: 90,
						maxWidth: 160,
						...columnCentered(),
					},
					{
						headerName: 'Mese approvato',
						field: 'monthsApprovedDate',
						minWidth: 120,
						valueGetter: (params: { data: { monthsApprovedDate: number } }) => {
							if (!params.data.monthsApprovedDate) return '';
							return 'M' + params.data.monthsApprovedDate;
						},
						...columnCentered(),
					},
					{
						headerName: 'Contributo Incassato €',
						headerTooltip: 'Contributo Incassato €',
						field: 'cashedInAmount',
						valueGetter: (params: { data: { cashedInAmount: number } }) => {
							return formatToDecimals(params.data.cashedInAmount);
						},
						...columnToRight(),
					},
					{
						headerName: 'Incassato',
						field: 'cashedIn',
						cellRenderer: PeriodicReportingCheckCell,
						// minWidth: 90,
						maxWidth: 160,
						...columnCentered(),
					},
				]
			: []),
		{
			headerName: 'Data Prevista Inizio',
			field: 'startDate',
			sortable: true,
			maxWidth: 190,
			valueGetter: (params: { data: { startDate: string } }) => {
				return format(params.data.startDate, 'dd/MM/yyyy', { locale: it });
			},
			...columnCentered(),
		},
		{
			headerName: 'Mese previsto inizio',
			field: 'monthsReportingStartDate',
			valueGetter: (params: { data: { monthsReportingStartDate: string } }) => {
				if (!params.data.monthsReportingStartDate) return '';

				return 'M' + params.data.monthsReportingStartDate;
			},
			...columnCentered(),
		},
		{
			headerName: 'Data Prevista fine',
			field: 'endDate',
			sortable: true,
			maxWidth: 190,
			valueGetter: (params: { data: { endDate: string } }) => {
				return format(params.data.endDate, 'dd/MM/yyyy', { locale: it });
			},
			...columnCentered(),
		},
		{
			headerName: 'Mese previsto fine',
			field: 'monthsReportingEndDate',
			valueGetter: (params: { data: { monthsReportingEndDate: string } }) => {
				if (!params.data.monthsReportingEndDate) return '';

				return 'M' + params.data.monthsReportingEndDate;
			},
			...columnCentered(),
		},
	] as const;

	const actionColumn = userAccess.isGuest
		? {
				headerName: '',
				minWidth: 90,
				maxWidth: 90,
				cellRenderer: PeriodicReportingActionCell,
				cellRendererParams: {
					fetchData,
					fetchTotals,
					state,
					isEnabledToView,
					isProjectWp: isProjectWp,
					userCompanies,
					projectIndirectCostsPerc,
				},
				resizable: false,
				cellStyle: { textAlign: 'center', padding: 0 },
			}
		: null;

	const defaultColDef = {
		resizable: false,
		wrapText: true,
		autoHeight: true,
		// flex: 1,
		suppressMovable: true,
		cellStyle: { padding: '0 1rem' },
	};

	if (!rowData) return;
	return (
		<>
			{(hasPartnerAccess || isEnabledToView) && state === 'DRAFT' && rowData.length > 0 ? (
				<div className="d-flex justify-content-end mb-4">
					<Button onClick={() => setIsCreateModalOpen(true)}>
						<IcoPlus className="light-color ico-small plus new-button" />
						Crea report
					</Button>
				</div>
			) : (
				<div style={{ height: '54px' }} />
			)}
			<div className="ag-theme-alpine list-table">
				<AgGridReact
					rowData={rowData}
					columnDefs={[...columnDefs, ...(actionColumn ? [actionColumn] : [])]}
					defaultColDef={defaultColDef}
					domLayout="autoHeight"
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					tooltipMouseTrack={true}
					headerHeight={50}
					rowHeight={50}
					suppressCellFocus={true}
					localeText={localeText}
				/>
			</div>
			{(hasPartnerAccess || isEnabledToView) && state === 'DRAFT' && rowData.length === 0 ? (
				<div className="d-flex justify-content-center mt-3">
					<Button onClick={() => setIsCreateModalOpen(true)}>
						<IcoPlus className="light-color ico-small plus new-button" />
						Crea report
					</Button>
				</div>
			) : null}
		</>
	);
};

export default PeriodicReportingTable;
